import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const statusLabels: Record<string, string> = {
  not_started: "Not Started", in_progress: "In Progress", completed: "Completed", on_hold: "On Hold",
};
const statusBadge: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  not_started: "outline", in_progress: "secondary", completed: "default", on_hold: "destructive",
};

const EmployeeProgress = () => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("in_progress");
  const [progress, setProgress] = useState(0);
  const [filterCompany, setFilterCompany] = useState("");

  const { data: companies = [] } = useQuery({
    queryKey: ["emp-companies"],
    queryFn: async () => { const { data } = await supabase.from("companies").select("*").order("name"); return data ?? []; },
  });

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ["emp-updates", filterCompany],
    queryFn: async () => {
      let q = supabase.from("project_updates").select("*, companies(name)").order("created_at", { ascending: false });
      if (filterCompany) q = q.eq("company_id", filterCompany);
      const { data } = await q;
      return data ?? [];
    },
  });

  const resetForm = () => { setTitle(""); setContent(""); setStatus("in_progress"); setProgress(0); setSelectedCompany(""); setEditId(null); setShowForm(false); };

  const saveUpdate = useMutation({
    mutationFn: async () => {
      const payload = { company_id: selectedCompany, title: title.trim(), content: content.trim() || null, status, progress_percent: progress };
      if (editId) {
        const { error } = await supabase.from("project_updates").update(payload).eq("id", editId);
        if (error) throw error;
      } else {
        const { data: inserted, error } = await supabase.from("project_updates").insert(payload).select().single();
        if (error) throw error;
        await supabase.from("notifications").insert({ company_id: selectedCompany, type: "project_update", title: `New update: ${title.trim()}`, message: content.trim() || null, related_id: inserted.id });
      }
    },
    onSuccess: () => { resetForm(); qc.invalidateQueries({ queryKey: ["emp-updates"] }); qc.invalidateQueries({ queryKey: ["emp-update-count"] }); toast.success(editId ? "Update saved" : "Update posted to client"); },
    onError: () => toast.error("Failed to save update"),
  });

  const deleteUpdate = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("project_updates").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["emp-updates"] }); qc.invalidateQueries({ queryKey: ["emp-update-count"] }); toast.success("Update deleted"); },
    onError: () => toast.error("Failed to delete"),
  });

  const startEdit = (u: typeof updates[0]) => {
    setEditId(u.id); setSelectedCompany(u.company_id); setTitle(u.title); setContent(u.content ?? ""); setStatus(u.status ?? "in_progress"); setProgress(u.progress_percent ?? 0); setShowForm(true);
  };

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Project Updates</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Milestones, progress, and client-facing updates</p>
        </div>
        <Button size="sm" variant={showForm ? "outline" : "default"} className={showForm ? "" : "bg-teal hover:bg-teal-light text-white"} onClick={() => { resetForm(); setShowForm(!showForm); }}>
          {showForm ? <><X className="w-4 h-4 mr-1" /> Cancel</> : <><Plus className="w-4 h-4 mr-1" /> Post Update</>}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="font-display text-lg text-foreground mb-4">{editId ? "Edit Update" : "Post Project Update"}</h2>
            <form onSubmit={(e) => { e.preventDefault(); saveUpdate.mutate(); }} className="space-y-4">
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger><SelectValue placeholder="Select client company..." /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Update title" required />
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Describe the update in detail (optional)..." rows={4} />
              <div className="grid grid-cols-2 gap-4">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5 font-body">Progress: {progress}%</label>
                  <input type="range" min={0} max={100} value={progress} onChange={(e) => setProgress(Number(e.target.value))} className="w-full accent-teal" />
                </div>
              </div>
              <Button type="submit" disabled={saveUpdate.isPending} className="bg-teal hover:bg-teal-light text-white">
                {saveUpdate.isPending ? "Saving..." : editId ? "Save Changes" : "Post Update"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="mb-6">
        <Select value={filterCompany || "all"} onValueChange={(v) => setFilterCompany(v === "all" ? "" : v)}>
          <SelectTrigger className="min-w-[200px]"><SelectValue placeholder="All companies" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All companies</SelectItem>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : updates.length === 0 ? (
        <Card><CardContent className="p-16 text-center"><BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground font-body">No updates yet. Post your first one.</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {updates.map((u) => (
            <Card key={u.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-lg text-foreground font-display">{u.title}</h3>
                    <p className="text-xs text-muted-foreground font-body mt-0.5">
                      {(u.companies as { name: string } | null)?.name ?? "—"} · {new Date(u.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={statusBadge[u.status ?? "in_progress"]}>{statusLabels[u.status ?? "in_progress"]}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(u)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => { if (confirm(`Delete "${u.title}"?`)) deleteUpdate.mutate(u.id); }}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground font-body">Progress</span>
                  <span className="text-xs text-teal font-body font-medium">{u.progress_percent}%</span>
                </div>
                <Progress value={u.progress_percent ?? 0} className="h-2 mb-3" />
                {u.content && <p className="text-sm text-muted-foreground font-body whitespace-pre-wrap">{u.content}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeProgress;
